import React, { useState } from "react";
import type { BoardProps } from "../pages/room";
import { t } from "../i18n";
import { Button, cx } from "../ui";
import { SEAT_COLORS } from "./colors";

const PIP: Record<number, [number, number][]> = {
  0: [], 1: [[1, 1]], 2: [[0.5, 0.5], [1.5, 1.5]], 3: [[0.5, 0.5], [1, 1], [1.5, 1.5]],
  4: [[0.5, 0.5], [1.5, 0.5], [0.5, 1.5], [1.5, 1.5]], 5: [[0.5, 0.5], [1.5, 0.5], [1, 1], [0.5, 1.5], [1.5, 1.5]],
  6: [[0.5, 0.4], [1.5, 0.4], [0.5, 1], [1.5, 1], [0.5, 1.6], [1.5, 1.6]],
};

export function Tile({ a, b, vertical, hidden, onClick, active, label }: { a: number; b: number; vertical?: boolean; hidden?: boolean; onClick?: () => void; active?: boolean; label?: string }) {
  const W = vertical ? 2 : 4;
  const H = vertical ? 4 : 2;
  const half = (v: number, ox: number, oy: number) => PIP[v].map(([x, y], i) => <circle key={`${ox}${oy}${i}`} cx={ox + x} cy={oy + y} r="0.2" fill="#1A1240" />);
  const svg = (
    <svg viewBox={`0 0 ${W} ${H}`} className={cx("tile", vertical && "is-v", hidden && "is-hidden", active && "is-active")} aria-hidden={!onClick}>
      <rect x="0.04" y="0.04" width={W - 0.08} height={H - 0.08} rx="0.3" fill={hidden ? "#2E2275" : "#FFF4E0"} stroke="#1A1240" strokeWidth="0.06" />
      {!hidden && (
        <>
          {vertical ? <path d="M0.3 2H1.7" stroke="#1A1240" strokeWidth="0.06" /> : <path d="M2 0.3V1.7" stroke="#1A1240" strokeWidth="0.06" />}
          {half(a, 0, 0)}
          {vertical ? half(b, 0, 2) : half(b, 2, 0)}
        </>
      )}
    </svg>
  );
  return onClick ? (
    <button className="tile-btn" onClick={onClick} aria-label={label}>
      {svg}
    </button>
  ) : (
    svg
  );
}

export function DominoBoard({ view, seat, names, myTurn, move }: BoardProps) {
  const [picked, setPicked] = useState<number | null>(null);
  const playable: { tile: [number, number]; sides: ("left" | "right")[] }[] = view.playable ?? [];
  const isPlayable = (tile: [number, number]) => playable.find((p) => p.tile[0] === tile[0] && p.tile[1] === tile[1]);
  const pick = (i: number) => {
    const tile = view.hand[i];
    const p = isPlayable(tile);
    if (!p) return;
    if (p.sides.length === 1) {
      move({ type: "play", tile, side: p.sides[0] });
      setPicked(null);
    } else setPicked(i);
  };
  const opponents = Array.from({ length: view.n }, (_, i) => i).filter((i) => i !== seat);
  const pickedP = picked !== null ? isPlayable(view.hand[picked]) : null;
  return (
    <div className="domino">
      <div className="domino-opponents">
        {opponents.map((o) => (
          <div key={o} className={cx("opp", o === view.turn && "is-turn")}>
            <span style={{ color: SEAT_COLORS[o] }}>●</span> {names[o]}
            <span className="opp-tiles">
              {Array.from({ length: view.handCounts[o] }).map((_, i) => <Tile key={i} a={0} b={0} vertical hidden />)}
            </span>
          </div>
        ))}
      </div>
      <div className="domino-scores">
        {view.scores.map((sc: number, i: number) => (
          <span key={i}>
            {view.teams ? `${t.team} ${i + 1} (${names[i]} + ${names[i + 2]})` : names[i]}: <b>{sc}</b>
          </span>
        ))}
        <span className="muted">→ {view.options.target}</span>
        <span className="muted">{t.boneyard}: {view.boneyardCount}</span>
      </div>
      <div className="domino-chain" dir="ltr">
        {pickedP?.sides.includes("left") && <Button kind="ghost" onClick={() => { move({ type: "play", tile: view.hand[picked!], side: "left" }); setPicked(null); }}>◀ {t.left}</Button>}
        <div className="chain-tiles">
          {view.chain.map((tl: [number, number], i: number) => <Tile key={i} a={tl[0]} b={tl[1]} vertical={tl[0] === tl[1]} />)}
        </div>
        {pickedP?.sides.includes("right") && <Button kind="ghost" onClick={() => { move({ type: "play", tile: view.hand[picked!], side: "right" }); setPicked(null); }}>{t.right} ▶</Button>}
      </div>
      <div className="domino-hand" dir="ltr">
        {view.hand.map((tl: [number, number], i: number) => (
          <Tile key={`${tl[0]}${tl[1]}`} a={tl[0]} b={tl[1]} vertical active={myTurn && !!isPlayable(tl)} onClick={myTurn && isPlayable(tl) ? () => pick(i) : undefined} label={`${tl[0]}|${tl[1]}`} />
        ))}
      </div>
      <div className="board-controls">
        {myTurn && view.canDraw && <Button onClick={() => move({ type: "draw" })}>{t.draw}</Button>}
        {myTurn && view.canPass && <Button onClick={() => move({ type: "pass" })}>{t.pass}</Button>}
        {!myTurn && <span className="hint">{t.turnOf(names[view.turn] ?? "")}</span>}
      </div>
    </div>
  );
}
